import { useDeepCompareEffect, useUpdateEffect } from "ahooks";
import { List } from "antd";
import qs from "qs";
import React, { useCallback, useEffect, useState } from "react";
import FiltersButton from "./filters-button";
import axios from "../../utils/axios";
import { isNullish } from "../../utils";
import styles from "./index.module.scss";

export default function ServerPaginatedList({
  url,
  pageSize = 10,
  dataSource,
  onDocsChange,
  renderItem,
  style,
  filters,
  optionsBar,
  getHelpers,
  showRefresh = true,
  resetOnMount = false,
  resetTrigger,
  ...props
}) {
  const initialState = {
    current: 1,
    pageSize,
    ...filters?.reduce(
      (acc, filter) => ({ ...acc, [`filter.${filter.key}`]: "" }),
      {}
    ),
  };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState(initialState);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Reset to first page and clear data when resetTrigger changes (e.g., on reopen)
  // Only reset when resetTrigger changes (e.g., on reopen)
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setTableParams((prev) => ({ ...prev, current: 1 }));
      setData([]);
    }
  }, [resetTrigger]);

  // Infinite scroll: load more when scrolled to bottom
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loading) {
      loadMore();
    }
  };

  const loadMore = () => {
    if (!hasMore || loading) return;
    setTableParams({
      ...tableParams,
      current: (tableParams.current || 1) + 1,
    });
  };

  const onFiltersChange = useCallback((filters) => {
    setTableParams({
      ...tableParams,
      ...filters?.reduce(
        (acc, filter) => ({ ...acc, [`filter.${filter.key}`]: filter.value }),
        {}
      ),
    });
  }, []);

  useDeepCompareEffect(() => {
    fetchData();
  }, [tableParams]);

  useUpdateEffect(() => {
    if (typeof onDocsChange === "function" && Array.isArray(data))
      onDocsChange(data);
  }, [data]);

  const fetchData = () => {
    setLoading(true);
    const { current, pageSize, ...restParams } = tableParams;
    const [_url, _query = ""] = url.split("?");

    const query = qs.parse(_query);
    // pagination
    if (pageSize) {
      query.limit = pageSize;
      query.page = current;
    }
    // filters
    Object.entries(restParams).forEach(([key, value]) => {
      if (!key.startsWith("filter.")) return;
      if (!isNullish(value)) query[key.split("filter.")[1]] = value;
    });

    axios
      .get(`${_url}?${qs.stringify(query)}`)
      .then(({ data: respData }) => {
        const list = Array.isArray(respData)
          ? respData
          : Object.values(respData).find((val) => Array.isArray(val)) || [];

        // If loading first page, replace; else append
        setData((prev) => {
          const newData = (current === 1 ? [] : prev).concat(
            list.map((item, ix) => {
              return { ...item, key: item.id ?? (prev.length + ix).toString() };
            })
          );
          setTotal(
            respData.total ||
              (current === 1 ? list.length : prev.length + list.length)
          );
          return newData;
        });
        setHasMore(list.length === pageSize);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (typeof getHelpers === "function")
      getHelpers({
        data,
        fetchData,
        setData,
        setLoading,
        setTableParams,
        setTotal,
        tableParams,
      });
  }, [data, fetchData, setData, setLoading, setTableParams, setTotal]);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        {optionsBar}
        {filters?.length ? (
          <FiltersButton
            active={Object.entries(tableParams).some(
              ([key, value]) => key.startsWith("filter.") && !isNullish(value)
            )}
            filters={filters.map((filter) => ({
              ...filter,
              value: tableParams[`filter.${filter.key}`],
            }))}
            onFiltersChange={onFiltersChange}
          />
        ) : null}
        {/* {showRefresh && (
          <Tooltip title="Reload">
            <Button onClick={fetchData}>
              <ReloadOutlined />
            </Button>
          </Tooltip>
        )} */}
      </div>

      <div
        style={{ overflowY: "auto", maxHeight: style?.height || 400 }}
        onScroll={handleScroll}
      >
        <List
          dataSource={
            typeof dataSource === "function" ? dataSource(data) : data
          }
          loading={loading}
          renderItem={renderItem}
          style={style}
          {...props}
        />
        {loading && (
          <div style={{ textAlign: "center", padding: 8 }}>Loading...</div>
        )}
      </div>
    </div>
  );
}
